
import Vue from "vue";

export default Vue.extend({
  name: "ShowResultsTable",
  props: {
    results: { type: Array, default: () => [] },
    dogId: { type: String, default: "" },
  },
  data: function () {
    return {
      filterDescription: [] as string[],
      filterCategory: [] as string[],
      filterDates: [] as string[],
      dateMenu: false,
      headers: [
        { text: "Show Date", align: "left", value: "showDate" },
        { text: "Descr.", align: "left", value: "description" },
        { text: "City", align: "left", value: "city" },
        { text: "State", align: "left", value: "state" },
        { text: "Country", align: "left", value: "country" },
        { text: "Time", align: "left", value: "time" },
        { text: "Category", align: "left", value: "category" },
        { text: "Division", align: "left", value: "division" },
        { text: "Score", align: "left", value: "score" },
        { text: "Place", align: "left", value: "place" },
        { text: "Hit", align: "left", value: "hit" },
        { text: "PT Sched", align: "left", value: "ptSched" },
        { text: "# Competing", align: "left", value: "nCompeting" },
        { text: "SCCH Score", align: "left", value: "scchScore" },
        { text: "SCCH Major", align: "left", value: "isScchMajor" },
        { text: "Handler", align: "left", value: "handler" },
        { text: "Judge", align: "left", value: "judge" },
      ],
    };
  },
  computed: {
    allResults(): any[] {
      return (this.$props.results as any[]) ?? [];
    },
    hasResults(): boolean {
      return this.allResults.length > 0;
    },
    // Offer only the values this dog actually has, so no filter comes up empty.
    descriptionOptions(): string[] {
      return this.distinctValues(this.allResults, "description");
    },
    // Categories belong to a program, so narrow them once a program is chosen.
    categoryOptions(): string[] {
      const scope = this.allResults.filter((row: any) =>
        this.matches(this.$data.filterDescription, row.description)
      );

      return this.distinctValues(scope, "category");
    },
    resultDays(): string[] {
      return this.allResults
        .map((row: any) => this.dayOf(row))
        .filter((day: string) => day !== "")
        .sort();
    },
    earliestResultDate(): string {
      return this.resultDays.length ? this.resultDays[0] : "";
    },
    latestResultDate(): string {
      return this.resultDays.length ? this.resultDays[this.resultDays.length - 1] : "";
    },
    // The picker reports the two dates in click order, so read them low to high.
    dateRange(): string[] {
      return this.$data.filterDates.length === 2
        ? this.$data.filterDates.slice().sort()
        : [];
    },
    dateRangeText(): string {
      if (this.dateRange.length !== 2) return "";

      return this.formatDay(this.dateRange[0]) + " to " + this.formatDay(this.dateRange[1]);
    },
    isFiltered(): boolean {
      return (
        this.$data.filterDescription.length > 0 ||
        this.$data.filterCategory.length > 0 ||
        this.dateRange.length === 2
      );
    },
    filteredResults(): any[] {
      if (!this.isFiltered) return this.allResults;

      return this.allResults.filter(
        (row: any) =>
          this.matches(this.$data.filterDescription, row.description) &&
          this.matches(this.$data.filterCategory, row.category) &&
          this.withinDateRange(row)
      );
    },
  },
  methods: {
    // Stored values carry padding, so compare and display them trimmed.
    distinctValues: function (rows: any[], field: string): string[] {
      const values = rows
        .map((row: any) => (row[field] ?? "").trim())
        .filter((value: string) => value !== "");

      return Array.from(new Set(values)).sort();
    },
    matches: function (selected: string[], value: string): boolean {
      return selected.length === 0 || selected.indexOf((value ?? "").trim()) >= 0;
    },
    // The picker yields a bare yyyy-MM-dd, which new Date() reads as UTC midnight and
    // then renders a day earlier in any western zone. Format the parts directly.
    formatDay: function (day: string): string {
      const parts = (day ?? "").split("-");

      return parts.length === 3 ? parts[1] + "/" + parts[2] + "/" + parts[0] : "";
    },
    dayOf: function (row: any): string {
      return (row.showDate ?? "").substring(0, 10);
    },
    withinDateRange: function (row: any): boolean {
      if (this.dateRange.length !== 2) return true;

      const day = this.dayOf(row);

      return day !== "" && day >= this.dateRange[0] && day <= this.dateRange[1];
    },
    onDateRangeChange: function () {
      if (this.$data.filterDates.length === 2) this.$data.dateMenu = false;
    },
    clearFilters: function () {
      this.filterDescription = [];
      this.filterCategory = [];
      this.filterDates = [];
    },
    csvExport: function () {
      const filename = `ShowResults_${this.$props.dogId?.trim()}_${new Date().toLocaleDateString()}.csv`;
      const content = this.filteredResults;
      const keys = Object.keys(content[0]);

      const commaSeparatedString = [
        keys.join(","),
        content.map((row: any) => keys.map((key) => row[key]).join(",")).join("\n"),
      ].join("\n");

      const csvBlob = new Blob([commaSeparatedString]);
      const link = document.createElement("a");
      const url = URL.createObjectURL(csvBlob);

      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
  watch: {
    // A category the chosen program does not contain would filter everything out.
    filterDescription: function () {
      const allowed = this.categoryOptions;
      this.filterCategory = this.filterCategory.filter(
        (chosen: string) => allowed.indexOf(chosen) >= 0
      );
    },
  },
});
